import { ModuleHistory, ModuleRevisionSnapshot } from './module-history';
import { LearnerFormComparison } from './learner-form-comparisons';
import { UserPreferences } from './user-preferences';
import { UserGroupsDto } from './dtos';
import { ContactUsValues } from './contact-us';
import { ModuleUsageDto } from './module-usage';
import { AdminTasksDto, AdminTaskSummary } from './admin-tasks';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { AdminAnalyticsDto } from './admin-analytics';
import { AdminFinanceDto, FinancePurchaseDetails } from './admin-finance';
import { AppState, GetTokenSilentlyOptions, IdToken, RedirectLoginOptions } from '@auth0/auth0-react';
import Config from './config';
import { certificateImageUrl, certificatePublicUrl } from './certificates';
import {
    AiRequestDto,
    LearnerCertificateDto,
    PendingCertificateNameDto,
    AdminCertificateDto,
    AdminCertificatesDto,
    AiResponseDto,
    CourseDto,
    CourseFeedbackInputDto,
    CourseFeedbackPromptDto,
    PublicReviewsDto,
    AdminReviewDto,
    AdminReviewsDto,
    ReviewFiltersDto,
    ListCourseDto,
    CreateSessionEventDto,
    FinishSessionDto,
    GetCoursesApiResponseDto,
    GetCourseUsersApiResponseDto,
    GetModulesApiResponseDto,
    GetModuleUsersApiResponseDto,
    GetMyModulesApiResponseDto,
    GetLearnerModuleAnswersDto,
    LearnerFieldAnswer,
    GetThemesDto,
    GetUserCoursesApiResponseDto,
    UserCourseRecordDto,
    GetUserModulesApiResponseDto,
    GetUsersApiResponseDto,
    GetUserSessionEventsApiResponseDto,
    GetUserQuizResultsApiResponseDto,
    GetUserQuizOptionsApiResponseDto,
    UserQuizFilter,
    GetUserSessionsApiResponseDto,
    GetUserStatsApiResponseDto,
    LearningPathDto,
    LearningInsightsDto,
    MessageDto,
    ModuleDto,
    ModuleFolderDto,
    CourseFolderDto,
    MyModuleDto,
    PostLoginDto,
    PostLoginResponseDto,
    SearchSessionsApiResponseDto,
    ServiceBookingInputDto,
    ServiceDto,
    ServiceListDto,
    SessionDto,
    SessionSearchDto,
    SessionEventDto,
    SetCourseAssignmentsDto,
    SetModuleAssignmentsDto,
    SetUserAssignmentsDto,
    SetUserCourseAssignmentsDto,
    StartSessionDto,
    ThemeCategory,
    ThemeNode,
    UploadFileDto,
    UserDto } from './dtos';
import { UserProfile } from './user';
import { CourseLoyaltyDto, CoursePromoDto, PaymentConfigDto, PurchaseDto, PurchaseQuoteDto,
    ProtectedStorageEntryDto, ProtectedStorageListingDto, ProtectedStorageDeleteDto, ModuleUseAgreementDto } from './dtos';

export class Api {
  private getToken: (options: GetTokenSilentlyOptions) => Promise<GetTokenSilentlyOptions | string>;
  private loginWithRedirect: (options?: RedirectLoginOptions<AppState>) => Promise<void>;
  private getIdTokenClaims: () => Promise<IdToken | undefined>;

  private POST_LOGIN_API: string = "/api/post-login.php";
  private USER_API: string = "/api/user.php";
  private MODULE_API: string = "/api/module.php";
  private ME_API: string = "/api/me.php";
  private SESSION_API: string = "/api/session.php";
  private COURSE_API: string = "/api/course.php";
  private UPLOAD_API: string = "/api/upload.php";
  // private CONFIG_API: string = "/api/config-api.php"; // Reserved for future public config values.
  private AI_API: string = "/api/ai.php";
  private THEME_API: string = "/api/theme.php";
  private SERVICE_API: string = "/api/services.php";
  private PROTECTED_STORAGE_API: string = "/api/protected_storage.php";

  constructor(getToken: (options: GetTokenSilentlyOptions) => Promise<GetTokenSilentlyOptions | string>,
    loginWithRedirect: (options?: RedirectLoginOptions<AppState>) => Promise<void>,
    getIdTokenClaims: () => Promise<IdToken | undefined>) {
    this.getToken = getToken;
    this.loginWithRedirect = loginWithRedirect;
    this.getIdTokenClaims = getIdTokenClaims;
  }

  async getAccessTokenWithConsent(forUserInfo: boolean): Promise<string | void> {
    const userInfoAudience: string = `https://${Config.auth0.domain}/userinfo`;
    try {
      const token: any = await this.getToken({
        authorizationParams: {
          audience: forUserInfo ? userInfoAudience : Config.auth0.audience,
          scope: "openid profile email"
        }
      });
      return token;
    } catch (e: any) {
      if (e.error === "consent_required" || e.error === "login_required") {
        const token: any = await this.loginWithRedirect({
            authorizationParams: {
                audience: forUserInfo ? userInfoAudience : Config.auth0.audience,
                scope: "openid profile email",
                prompt: "consent"
            }
        });
        return token;
      }
      throw e;
    }
  }

  async apiCreateRequestConfig(method: string, uri: string, data: FormData = new FormData(), hasFile: boolean = false): Promise<AxiosRequestConfig> {
    // const isFreePlan: boolean = Config.auth0.is_free_plan;
    // const token: any = isFreePlan ? (await this.getIdTokenClaims())?.__raw : (await this.getAccessTokenWithConsent(true));
    const token: any = await this.getAccessTokenWithConsent(false);

    const result: AxiosRequestConfig = {
      url: uri,
      method: method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(hasFile ? {} : { 'Content-Type': `application/json` })
      },
      data: data
    };
    return result;
  }

  async get <T>(uri: string): Promise<T> {
    const requestConfig: AxiosRequestConfig = await this.apiCreateRequestConfig('GET', uri);
    const response: AxiosResponse<T> = await axios.request(requestConfig);
    return response.data;
  }

  async post <T>(uri: string, data: FormData | null, body: any | null): Promise<T> {
    if (data) {
        const requestConfig: AxiosRequestConfig = await this.apiCreateRequestConfig('POST', uri, data, true);
        const response: AxiosResponse<T> = await axios.request(requestConfig);
        return response.data;
    } else {
        const requestConfig: AxiosRequestConfig = await this.apiCreateRequestConfig('POST', uri, undefined);
        const response: AxiosResponse<T> = await axios.post(uri, body, requestConfig);
        return response.data;
    }
  }

  async put <T>(uri: string, data: FormData | null, body: any | null): Promise<T> {
    if (data) {
        const requestConfig: AxiosRequestConfig = await this.apiCreateRequestConfig('PUT', uri, data, true);
        const response: AxiosResponse<T> = await axios.request(requestConfig);
        return response.data;
    } else {
        const requestConfig: AxiosRequestConfig = await this.apiCreateRequestConfig('PUT', uri, undefined);
        const response: AxiosResponse<T> = await axios.put(uri, body, requestConfig);
        return response.data;
    }
  }

  async delete <T>(uri: string): Promise<T> {
    const requestConfig: AxiosRequestConfig = await this.apiCreateRequestConfig('DELETE', uri);
    const response: AxiosResponse<T> = await axios.request(requestConfig);
    return response.data;
  }

  private objectToFormData(obj: any): FormData {
    const formData: FormData = new FormData();
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value: any = obj[key];
        if (value instanceof File) {
          formData.append(key, value, value.name);
        } else{
          formData.append(key, value);
        }
      }
    }
    return formData;
  }

  async postLogin(firstname: string | undefined,
        lastname: string | undefined,
        picture: string | undefined,
        email: string | undefined): Promise<PostLoginResponseDto> {
    const dto: PostLoginDto = { event: "postLogin", firstName: firstname ?? '', lastName: lastname ?? '', picture: picture ?? '', email: email ?? '' };
    const result: PostLoginResponseDto = await this.post<PostLoginResponseDto>(Config.apiRoot + this.POST_LOGIN_API, null, dto);
    return result;
  }

  async saveMyPreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    return this.put<UserPreferences>(Config.apiRoot + `${this.ME_API}/preferences`, null, preferences);
  }

  async submitContactUs(message: ContactUsValues): Promise<void> {
    // Public enquiries must not trigger sign-in or request an access token.
    await axios.post(Config.apiRoot + '/api/contact-us.php', message);
  }

  buildQueryString(page: number, pageSize: number, orderBy: string, orderDirection: string, 
    filterBy: string, filterValue: string): string {
    const query: URLSearchParams = new URLSearchParams({});
    if (page > 0) {
      query.append("page", page.toString());
    }
    if (pageSize > 0) {
      query.append("pageSize", pageSize.toString());
    }

    if ((orderBy ?? '').length > 0) {
      query.append("orderBy", orderBy);
      if ((orderDirection ?? '').length > 0) {
        query.append("orderDirection", orderDirection);
        }
    }
    
    if ((filterBy ?? '').length > 0) {
      query.append("filterBy", filterBy);
      if ((filterValue ?? '').length > 0) {
        query.append("filterValue", filterValue);
      }
    }
    return query.toString();
  }

  async getUser(sub: string): Promise<UserDto> {    
    const result: UserDto = await this.get<UserDto>(Config.apiRoot + `${this.USER_API}/${sub}`);
    return result;
  }

  async updateUser(user: UserDto): Promise<UserDto> {    
    const result: UserDto = await this.put<UserDto>(Config.apiRoot + `${this.USER_API}/${user.sub}`, null, user);
    return result;
  }

  async getUsers(page: number, pageSize: number, orderBy: string, orderDirection: string, 
    filterBy: string, filterValue: string, groupId?: number | 'ungrouped' | null): Promise<GetUsersApiResponseDto> {
    const queryString: string = this.buildQueryString(page, pageSize, orderBy, orderDirection, filterBy, filterValue);
    const result: GetUsersApiResponseDto = await this.get<GetUsersApiResponseDto>(Config.apiRoot + `${this.USER_API}?${queryString}${groupId == null ? '' : `&group_id=${groupId}`}`);
    return result;
  }

  async getUserGroups(): Promise<UserGroupsDto> { return this.get<UserGroupsDto>(Config.apiRoot + '/api/user-groups.php'); }
  async createUserGroup(name: string): Promise<UserGroupsDto> { return this.post<UserGroupsDto>(Config.apiRoot + '/api/user-groups.php', null, {name}); }
  async renameUserGroup(id: number, name: string): Promise<UserGroupsDto> { return this.put<UserGroupsDto>(Config.apiRoot + `/api/user-groups.php/${id}`, null, {name}); }
  async deleteUserGroup(id: number): Promise<UserGroupsDto> { return this.delete<UserGroupsDto>(Config.apiRoot + `/api/user-groups.php/${id}`); }
  async changeUserGroupMembers(id: number | null, userKeys: string[], action: 'move' | 'remove'): Promise<UserGroupsDto> {
    return this.post<UserGroupsDto>(Config.apiRoot + `/api/user-groups.php/${id ?? 'ungrouped'}/members`, null, {user_keys: userKeys, action});
  }

  async getModules(page: number, pageSize: number, orderBy: string, orderDirection: string, 
    filterBy: string, filterValue: string, folderId?: number | null): Promise<GetModulesApiResponseDto> {
    const queryString: string = this.buildQueryString(page, pageSize, orderBy, orderDirection, filterBy, filterValue);
    const result: GetModulesApiResponseDto = await this.get<GetModulesApiResponseDto>(Config.apiRoot + `${this.MODULE_API}?${queryString}${folderId === undefined ? "" : `&folder_id=${folderId === null ? "root" : folderId}`}`);
    return result;
  }

  async getModuleFolders(): Promise<{ data: ModuleFolderDto[] }> {
    return this.get(Config.apiRoot + '/api/module-folders.php');
  }

  async createModuleFolder(name: string, parent_id: number | null): Promise<{ data: ModuleFolderDto[] }> {
    return this.post(Config.apiRoot + '/api/module-folders.php', null, { name, parent_id });
  }

  async updateModuleFolder(id: number, changes: { name?: string; parent_id?: number | null }): Promise<{ data: ModuleFolderDto[] }> {
    return this.put(Config.apiRoot + `/api/module-folders.php/${id}`, null, changes);
  }

  async deleteModuleFolder(id: number): Promise<{ data: ModuleFolderDto[] }> {
    return this.delete(Config.apiRoot + `/api/module-folders.php/${id}`);
  }

  async moveModulesToFolder(module_keys: string[], folder_id: number | null): Promise<MessageDto> {
    return this.post(Config.apiRoot + '/api/module-folders.php/move-modules', null, { module_keys, folder_id });
  }

  async getCourseFolders(): Promise<{ data: CourseFolderDto[] }> {
    return this.get(Config.apiRoot + '/api/course-folders.php');
  }

  async createCourseFolder(name: string, parent_id: number | null): Promise<{ data: CourseFolderDto[] }> {
    return this.post(Config.apiRoot + '/api/course-folders.php', null, { name, parent_id });
  }

  async updateCourseFolder(id: number, changes: { name?: string; parent_id?: number | null }): Promise<{ data: CourseFolderDto[] }> {
    return this.put(Config.apiRoot + `/api/course-folders.php/${id}`, null, changes);
  }

  async deleteCourseFolder(id: number): Promise<{ data: CourseFolderDto[] }> {
    return this.delete(Config.apiRoot + `/api/course-folders.php/${id}`);
  }

  async moveCoursesToFolder(course_keys: string[], folder_id: number | null): Promise<MessageDto> {
    return this.post(Config.apiRoot + '/api/course-folders.php/move-courses', null, { course_keys, folder_id });
  }

  async getModule(key: string): Promise<ModuleDto | null> {
    const result: ModuleDto = await this.get<ModuleDto>(Config.apiRoot + `${this.MODULE_API}/${key}`);
    return result;
  }

  async createModule(module: ModuleDto): Promise<ModuleDto | MessageDto | null> {
    const result: ModuleDto | MessageDto = 
        await this.post<ModuleDto | MessageDto>(Config.apiRoot + this.MODULE_API, null, module);
    return result;
  }

  async updateModule(module: ModuleDto): Promise<ModuleDto | MessageDto | null> {
    const result: ModuleDto | MessageDto = 
        await this.put<ModuleDto | MessageDto>(Config.apiRoot + `${this.MODULE_API}/${module.module_key}`, null, module);
    return result;
  }

  async deleteModule(key: string): Promise<MessageDto> {
    const message: MessageDto = await this.delete<MessageDto>(Config.apiRoot + `${this.MODULE_API}/${key}`);
    return message;
  }

  async touchModules(keys: string[]): Promise<ModuleDto[] | null> {
    const result: any = await this.post<any>(Config.apiRoot + `${this.MODULE_API}/touch`, null, keys);
    return result.data as ModuleDto[];
  }

  async getUserModules(sub: string): Promise<GetUserModulesApiResponseDto> {
    const result: GetUserModulesApiResponseDto = await this.get<GetUserModulesApiResponseDto>(Config.apiRoot + `${this.USER_API}/modules/${sub}`);
    return result;
  }

  async getAllUserModules(sub: string, page: number, pageSize: number, orderBy: string, orderDirection: string, 
    filterBy: string, filterValue: string): Promise<GetMyModulesApiResponseDto> {
    const queryString: string = this.buildQueryString(page, pageSize, orderBy, orderDirection, filterBy, filterValue);
    const result: GetMyModulesApiResponseDto = await this.get<GetMyModulesApiResponseDto>(Config.apiRoot + `${this.USER_API}/modules/${sub}/all?${queryString}`);
    return result;
  }

  async getModuleHistory(key: string, before?: string, signal?: AbortSignal): Promise<ModuleHistory> {
    const url = Config.apiRoot + `/api/module-history.php/${encodeURIComponent(key)}` + (before ? `?before=${encodeURIComponent(before)}` : '');
    const config = await this.apiCreateRequestConfig('GET', url);
    return (await axios.request<ModuleHistory>({ ...config, signal })).data;
  }

  async getModuleRevision(key: string, id: string, signal?: AbortSignal): Promise<ModuleRevisionSnapshot> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/module-history.php/${encodeURIComponent(key)}/${encodeURIComponent(id)}`);
    return (await axios.request<ModuleRevisionSnapshot>({ ...config, signal })).data;
  }

  async getModuleUsage(key: string, signal?: AbortSignal): Promise<ModuleUsageDto> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/module-usage.php/${encodeURIComponent(key)}`);
    return (await axios.request<ModuleUsageDto>({ ...config, signal })).data;
  }

  async getModuleUsers(key: string): Promise<GetModuleUsersApiResponseDto> {
    const result: GetModuleUsersApiResponseDto = await this.get<GetModuleUsersApiResponseDto>(Config.apiRoot + `${this.MODULE_API}/users/${key}`);
    return result;
  }

  async saveUserModules(sub: string, module_keys: string[]): Promise<GetUserModulesApiResponseDto | MessageDto> {
    const requestDto: SetUserAssignmentsDto = { modules: module_keys };
    const result: GetUserModulesApiResponseDto | MessageDto = 
        await this.post<GetUserModulesApiResponseDto | MessageDto>(Config.apiRoot + `${this.USER_API}/modules/${sub}`, null, requestDto);
    return result;
  }

  async saveModuleUsers(module_key: string, user_keys: string[]): Promise<GetModuleUsersApiResponseDto | MessageDto> {
    const requestDto: SetModuleAssignmentsDto = { users: user_keys };
    const result: GetModuleUsersApiResponseDto | MessageDto = 
        await this.post<GetModuleUsersApiResponseDto | MessageDto>(Config.apiRoot + `${this.MODULE_API}/users/${module_key}`, null, requestDto);
    return result;
  }

  async getMyProfile(): Promise<UserDto | null> {
    const result: UserDto = await this.get<UserDto>(Config.apiRoot + `${this.ME_API}`);
    return result;
  }

  async updateMyProfile(profile: UserProfile): Promise<UserDto | null> {
    const result: UserDto = await this.put<UserDto>(Config.apiRoot + `${this.ME_API}`, null, profile);
    return result;
  }

  async getMyModules(page: number, pageSize: number, orderBy: string, orderDirection: string, 
    filterBy: string, filterValue: string): Promise<GetMyModulesApiResponseDto> {
    const queryString: string = this.buildQueryString(page, pageSize, orderBy, orderDirection, filterBy, filterValue);
    const result: GetMyModulesApiResponseDto = await this.get<GetMyModulesApiResponseDto>(Config.apiRoot + `${this.ME_API}/modules?${queryString}`);
    return result;
  }

  async getModuleUseAgreement(key: string, signal?: AbortSignal): Promise<ModuleUseAgreementDto> {
    const uri = Config.apiRoot + `${this.ME_API}/modules/${encodeURIComponent(key)}/use-agreement`;
    const config = await this.apiCreateRequestConfig('GET', uri);
    return (await axios.request<ModuleUseAgreementDto>({ ...config, signal })).data;
  }

  async acceptModuleUseAgreement(key: string, version: string): Promise<ModuleUseAgreementDto> {
    const result = await this.post<ModuleUseAgreementDto>(Config.apiRoot + `${this.ME_API}/modules/${encodeURIComponent(key)}/use-agreement`,
        null, { accepted: true, notice_version: version });
    if (result?.accepted !== true || !result.accepted_at) throw new Error('The server did not confirm your agreement. Please try again.');
    return result;
  }

  async getMyModule(key: string): Promise<MyModuleDto | null> {
    const result: MyModuleDto = await this.get<MyModuleDto>(Config.apiRoot + `${this.ME_API}/modules/${key}`);
    return result;
  }
   
  async startSession(module_key: string): Promise<SessionDto | null> {
    const result: SessionDto = await this.post<SessionDto>(Config.apiRoot + `${this.ME_API}/session`, null, { module_key: module_key } as StartSessionDto);
    return result;
  }

  async endSession(session_id: string): Promise<SessionDto | null> {
    const result: SessionDto = await this.put<SessionDto>(Config.apiRoot + `${this.ME_API}/session`, null, { session_id: session_id } as FinishSessionDto);
    return result;
  }

  async getUserStats(sub: string): Promise<GetUserStatsApiResponseDto | null> {
    const result: GetUserStatsApiResponseDto = await this.get<GetUserStatsApiResponseDto>(Config.apiRoot + `${this.USER_API}/stats/${sub}`);
    return result;
  }

  async createSessionEvent(session_id: string, payload: string): Promise<SessionEventDto | null> {
    const result: SessionEventDto = await this.post<SessionEventDto>(Config.apiRoot + `${this.ME_API}/session/${session_id}/event`,
        null, { payload: payload } as CreateSessionEventDto);
    return result;
  }

  async getUserSessions(sub: string, module_key: string): Promise<GetUserSessionsApiResponseDto | null> {
    const result: GetUserSessionsApiResponseDto = await this.get<GetUserSessionsApiResponseDto>(
        Config.apiRoot + `${this.USER_API}/sessions/${sub}/module/${module_key}`);
    return result;
  }

  async getUserSessionEvents(sub: string, session_id: string): Promise<GetUserSessionEventsApiResponseDto | null> {
    const result: GetUserSessionEventsApiResponseDto = await this.get<GetUserSessionEventsApiResponseDto>(
        Config.apiRoot + `${this.USER_API}/sessions/${sub}/session/${session_id}`);
    return result;
  }

  async submitCourseFeedback(sessionId: string | undefined, feedback: CourseFeedbackInputDto): Promise<void> {
    const uri = sessionId ? `${this.ME_API}/session/${encodeURIComponent(sessionId)}/feedback` : `${this.ME_API}/feedback`;
    const result = await this.post<{ saved: boolean }>(Config.apiRoot + uri, null, feedback);
    if (result?.saved !== true) throw new Error('The server did not confirm your feedback. Please try again.');
  }

  async getFeedbackCourses(signal?: AbortSignal): Promise<CourseFeedbackPromptDto[]> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `${this.ME_API}/feedback-courses`);
    const response = await axios.request<{ data: CourseFeedbackPromptDto[] }>({ ...config, signal });
    return response.data.data;
  }

  async sendCourseCompletionEmail(courseKey: string, email: string): Promise<void> {
    const result = await this.post<{ sent: boolean }>(Config.apiRoot + '/api/course-completion-email.php', null, { course_key: courseKey, email });
    if (result?.sent !== true) throw new Error('The server did not confirm your email was sent. Please try again.');
  }

  async getMyCertificates(signal?: AbortSignal): Promise<LearnerCertificateDto[]> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `${this.ME_API}/certificates`);
    const response = await axios.request<{ data: LearnerCertificateDto[] }>({ ...config, signal });
    return response.data.data;
  }

  async getPendingCertificateNames(signal?: AbortSignal): Promise<PendingCertificateNameDto[]> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `${this.ME_API}/certificates/pending-names`);
    const response = await axios.request<{ data: PendingCertificateNameDto[] }>({ ...config, signal });
    return response.data.data;
  }

  async confirmCertificateName(key: string, name: string): Promise<void> {
    const result = await this.post<{ confirmed: boolean; recipient_name: string }>(Config.apiRoot + `${this.ME_API}/certificates/${encodeURIComponent(key)}/name`, null,
        { recipient_name: name.trim(), name_verified: true });
    if (result?.confirmed !== true || result.recipient_name !== name.trim()) throw new Error('The server did not confirm your certificate name. Please try again.');
  }

  getCertificatePublicUrl(key: string): string {
    return certificatePublicUrl(key);
  }

  async requestCertificatePrint(key: string, delivery: { recipient_name: string; recipient_email: string; postal_address: string }): Promise<void> {
    await this.post(Config.apiRoot + '/api/certificate-print-requests.php?key=' + encodeURIComponent(key), null, delivery);
  }

  static async getPublicCertificateStatus(key: string, signal?: AbortSignal): Promise<{ status: 'ready' | 'pending_name'; course_name: string }> {
    const response = await axios.get<{ status: 'ready' | 'pending_name'; course_name: string }>(`${certificateImageUrl(key)}&status=1`, { signal });
    if (!['ready', 'pending_name'].includes(response.data?.status) || typeof response.data.course_name !== 'string') {
        throw new Error('Could not load certificate status.');
    }
    return response.data;
  }

  getCertificateImageUrl(key: string): string {
    return certificateImageUrl(key);
  }

  async downloadCertificate(key: string, signal?: AbortSignal): Promise<Blob> {
    const response = await axios.get<Blob>(certificateImageUrl(key), { signal, responseType: 'blob' });
    if (response.data.type !== 'image/png') throw new Error('The certificate image could not be downloaded.');
    return response.data;
  }

  async getAdminCertificates(page: number, filters: { user?: string; course?: string; status?: string } = {}, signal?: AbortSignal): Promise<AdminCertificatesDto> {
    const query = new URLSearchParams({ page: String(page) });
    if (filters.user) query.set('user', filters.user);
    if (filters.course) query.set('course', filters.course);
    if (filters.status && filters.status !== 'all') query.set('status', filters.status);
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-certificates.php?${query}`);
    return (await axios.request<AdminCertificatesDto>({ ...config, signal })).data;
  }

  async getAdminCertificate(key: string, signal?: AbortSignal): Promise<AdminCertificateDto> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-certificates.php/${encodeURIComponent(key)}`);
    return (await axios.request<AdminCertificateDto>({ ...config, signal })).data;
  }

  async getAdminCertificatePreview(key: string, signal?: AbortSignal): Promise<Blob> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-certificates.php/${encodeURIComponent(key)}/preview`);
    return (await axios.request<Blob>({ ...config, signal, responseType: 'blob' })).data;
  }

  async setCertificateRevoked(key: string, revoked: boolean): Promise<AdminCertificateDto> {
    const result = await this.put<AdminCertificateDto>(Config.apiRoot + `/api/admin-certificates.php/${encodeURIComponent(key)}`, null, { revoked });
    if (result?.key !== key || Boolean(result.revoked_at) !== revoked) throw new Error('The server did not confirm the certificate status.');
    return result;
  }

  async getPublicReviews(page: number, course: string | null, signal?: AbortSignal, sort = 'newest'): Promise<PublicReviewsDto> {
    const query = new URLSearchParams({ page: String(page) });
    query.set('sort', sort);
    if (course) query.set('course', course);
    const response = await axios.get<PublicReviewsDto>(Config.apiRoot + `/api/reviews.php?${query}`, { signal });
    return response.data;
  }

  async getAdminReviews(page: number, filters: ReviewFiltersDto, pageSize: number, signal?: AbortSignal): Promise<AdminReviewsDto> {
    const query = new URLSearchParams({ page: String(page), page_size: String(pageSize), shared: filters.shared, approved: filters.approved, stars: filters.stars });
    if (filters.course) query.set('course', filters.course);
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-reviews.php?${query}`);
    const response = await axios.request<AdminReviewsDto>({ ...config, signal });
    return response.data;
  }

  async getAdminReview(id: string, signal?: AbortSignal): Promise<AdminReviewDto> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-reviews.php/${encodeURIComponent(id)}`);
    const response = await axios.request<AdminReviewDto>({ ...config, signal });
    return response.data;
  }

  async approveReview(id: number): Promise<void> {
    const result = await this.post<{ approved: boolean }>(Config.apiRoot + `/api/admin-reviews.php/${id}/approve`, null, {});
    if (result?.approved !== true) throw new Error('The server did not confirm approval. Please try again.');
  }

  async unapproveReview(id: number): Promise<void> {
    const result = await this.post<{ approved: boolean }>(Config.apiRoot + `/api/admin-reviews.php/${id}/unapprove`, null, {});
    if (result?.approved !== false) throw new Error('The server did not confirm that approval was removed. Please try again.');
  }

  async clearMyDebugData(category: 'sessions' | 'forms' | 'agreements' | 'feedback' | 'purchases'): Promise<Record<string, number>> {
    const result = await this.delete<{ cleared: string; deleted: Record<string, number> }>(
        Config.apiRoot + `/api/admin-debug.php/${category}`);
    if (result?.cleared !== category || !result.deleted) throw new Error('The server did not confirm the reset. Please try again.');
    return result.deleted;
  }

  async getDebugModules(signal?: AbortSignal): Promise<{ module_key: string; name: string }[]> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + '/api/admin-debug.php/modules');
    const response = await axios.request<{ data: { module_key: string; name: string }[] }>({ ...config, signal });
    return response.data.data;
  }

  async markMyDebugModuleNotComplete(moduleKey: string): Promise<void> {
    const result = await this.post<{ marked_not_complete: boolean; module_key: string }>(
        Config.apiRoot + `/api/admin-debug.php/modules/${encodeURIComponent(moduleKey)}/mark-not-complete`, null, {});
    if (result?.marked_not_complete !== true || result.module_key !== moduleKey) throw new Error('The server did not confirm the module reset. Please try again.');
  }

  async clearMyDebugModuleData(moduleKey: string, category: 'sessions' | 'forms' | 'certificates' | 'reviews'): Promise<Record<string, number>> {
    const result = await this.delete<{ module_key: string; cleared: string; deleted: Record<string, number> }>(
        Config.apiRoot + `/api/admin-debug.php/modules/${encodeURIComponent(moduleKey)}/${category}`);
    if (result?.module_key !== moduleKey || result.cleared !== category || !result.deleted) throw new Error('The server did not confirm the module reset. Please try again.');
    return result.deleted;
  }

  async getMyModuleAnswers(key: string): Promise<GetLearnerModuleAnswersDto> {
    return this.get<GetLearnerModuleAnswersDto>(Config.apiRoot + `${this.ME_API}/modules/${encodeURIComponent(key)}/answers`);
  }

  async getMyFormComparisons(moduleKey: string, fragmentKey: string, courseKey?: string | null): Promise<{ data: LearnerFormComparison[] }> {
    const query = new URLSearchParams({ fragment_key: fragmentKey });
    if (courseKey) query.set('course_key', courseKey);
    return this.get(Config.apiRoot + `${this.ME_API}/modules/${encodeURIComponent(moduleKey)}/comparisons?${query}`);
  }

  async saveMyModulePosition(moduleKey: string, fragmentKey: string): Promise<void> {
    const config = await this.apiCreateRequestConfig('POST', Config.apiRoot + `${this.ME_API}/modules/${encodeURIComponent(moduleKey)}/position`);
    await axios.request({ ...config, data: { fragment_key: fragmentKey }, timeout: 5000 });
  }

  async saveMyFragmentAnswers(moduleKey: string, fragmentKey: string, answers: Record<string, LearnerFieldAnswer>, confirmFillOnce = false): Promise<{ locked: boolean }> {
    const result = await this.post<{ saved: boolean; locked?: boolean }>(
        Config.apiRoot + `${this.ME_API}/modules/${encodeURIComponent(moduleKey)}/answers`, null,
        { fragment_key: fragmentKey, answers, ...(confirmFillOnce ? { confirm_fill_once: true } : {}) });
    if (result?.saved !== true) throw new Error('The server did not confirm that your answers were saved.');
    return { locked: result.locked === true };
  }

  async getUserQuizResults(sub: string, filter: UserQuizFilter): Promise<GetUserQuizResultsApiResponseDto> {
    const query = new URLSearchParams();
    if (filter.course_key !== undefined) query.set('course_key', filter.course_key);
    else query.set('module_key', filter.module_key);
    return this.get<GetUserQuizResultsApiResponseDto>(
        Config.apiRoot + `${this.USER_API}/quiz-results/${encodeURIComponent(sub)}?${query}`);
  }

  async getUserQuizOptions(sub: string): Promise<GetUserQuizOptionsApiResponseDto> {
    return this.get<GetUserQuizOptionsApiResponseDto>(
        Config.apiRoot + `${this.USER_API}/quiz-result-options/${encodeURIComponent(sub)}`);
  }

  async getSession(key: string): Promise<SessionSearchDto> {
    return this.get<SessionSearchDto>(Config.apiRoot + `${this.SESSION_API}/${encodeURIComponent(key)}`);
  }

  async searchSessions(page: number, pageSize: number, orderBy: string, orderDirection: string, 
    filterBy: string, filterValue: string, exclude_empty: boolean): Promise<SearchSessionsApiResponseDto> {
    const stdQueryString: string = this.buildQueryString(page, pageSize, orderBy, orderDirection, filterBy, filterValue);
    const queryString: string = exclude_empty ? (((stdQueryString ?? '').length > 0 ? '&' : '') + 'exclude_empty=1') : '';
    const result: SearchSessionsApiResponseDto = await this.get<SearchSessionsApiResponseDto>(Config.apiRoot + `${this.SESSION_API}/?${stdQueryString}${queryString}`);
    return result;
  }

  async groomSessions(age: number, mode: string): Promise<MessageDto> {
    const queryString: string = `age=${age}&mode=${mode}`;
    const result: MessageDto = await this.delete<MessageDto>(Config.apiRoot + `${this.SESSION_API}/groom?${queryString}`);
    return result;
  }

  async searchCourses(page: number, pageSize: number, orderBy: string, orderDirection: string, 
    filterBy: string, filterValue: string): Promise<GetCoursesApiResponseDto> {
    const queryString: string = this.buildQueryString(page, pageSize, orderBy, orderDirection, filterBy, filterValue);
    const result: GetCoursesApiResponseDto = await this.get<GetCoursesApiResponseDto>(Config.apiRoot + `${this.COURSE_API}?${queryString}`);
    return result;
  }

  async getCourse(key: string): Promise<CourseDto | null> {
    const result: CourseDto = await this.get<CourseDto>(Config.apiRoot + `${this.COURSE_API}/${key}`);
    return result;
  }

  async getProducts(authenticated: boolean = true): Promise<{ data: ListCourseDto[] }> {
    if (authenticated) return this.get<{ data: ListCourseDto[] }>(Config.apiRoot + `${this.ME_API}/products`);
    const response = await axios.get<{ data: ListCourseDto[] }>(Config.apiRoot + '/api/products.php');
    return response.data;
  }

  async getPaymentConfig(): Promise<PaymentConfigDto> {
    return this.get(Config.apiRoot + '/api/payments.php?action=config');
  }

  async quoteCoursePurchase(course_key: string, promo_code: string): Promise<PurchaseQuoteDto> {
    return this.post(Config.apiRoot + '/api/payments.php?action=quote', null, { course_key, promo_code });
  }

  async createCourseCheckout(course_key: string, promo_code: string, quote: PurchaseQuoteDto): Promise<PurchaseDto> {
    return this.post(Config.apiRoot + '/api/payments.php?action=checkout', null, { course_key, promo_code,
      expected_amount_minor: quote.payable_amount_minor, expected_base_minor: quote.quoted_amount_minor });
  }

  async prepareCoursePurchase(course_key: string, promo_code: string = ''): Promise<{ quote: PurchaseQuoteDto; purchase: PurchaseDto | null }> {
    return this.post(Config.apiRoot + '/api/payments.php?action=prepare', null, { course_key, promo_code });
  }

  async getCoursePurchase(key: string): Promise<PurchaseDto> {
    return this.get(Config.apiRoot + '/api/payments.php?action=purchase&key=' + encodeURIComponent(key));
  }

  async getPurchaseHistory(): Promise<{ data: PurchaseDto[] }> {
    return this.get(Config.apiRoot + '/api/payments.php?action=history');
  }

  async getCoursePromos(course_key: string): Promise<{ data: CoursePromoDto[] }> {
    return this.get(Config.apiRoot + '/api/course-promos.php?course_key=' + encodeURIComponent(course_key));
  }

  async getCourseLoyalty(course_key: string): Promise<{ data: CourseLoyaltyDto[] }> {
    return this.get(Config.apiRoot + '/api/course-loyalty.php?course_key=' + encodeURIComponent(course_key));
  }

  async saveCourseLoyalty(course_key: string, rule: CourseLoyaltyDto, remove = false): Promise<{ data: CourseLoyaltyDto[] }> {
    return this.post(Config.apiRoot + '/api/course-loyalty.php?course_key=' + encodeURIComponent(course_key), null, { ...rule, remove });
  }

  async saveCoursePromo(course_key: string, promo: CoursePromoDto): Promise<{ data: CoursePromoDto[] }> {
    const result = await this.post<{ data: CoursePromoDto[] }>(Config.apiRoot + '/api/course-promos.php?course_key=' + encodeURIComponent(course_key), null, promo);
    const code = promo.code.trim().toUpperCase();
    const saved = result?.data?.find(row => promo.id !== undefined ? row.id === promo.id : row.code === code);
    if (!saved || saved.code !== code || Number(saved.discount_percent) !== Number(promo.discount_percent)
      || saved.enabled !== promo.enabled || saved.validity_mode !== promo.validity_mode
      || (promo.validity_mode === 'purchases' ? saved.purchase_limit !== promo.purchase_limit
        : saved.starts_on !== promo.starts_on || saved.ends_on !== promo.ends_on)) {
      throw new Error('The server did not confirm the promotion changes. Your edits have been kept. Please try saving again.');
    }
    return result;
  }

  // Services and bookings are public; these requests do not obtain an Auth0 token.
  async getServices(signal?: AbortSignal): Promise<ServiceListDto[]> {
    const response = await axios.get<{ data: ServiceListDto[] }>(Config.apiRoot + this.SERVICE_API, { signal });
    return response.data.data;
  }

  async getService(key: string, signal?: AbortSignal): Promise<ServiceDto> {
    const response = await axios.get<ServiceDto>(Config.apiRoot + `${this.SERVICE_API}/${encodeURIComponent(key)}`, { signal });
    return response.data;
  }

  async submitServiceBooking(key: string, booking: ServiceBookingInputDto): Promise<void> {
    await axios.post(Config.apiRoot + `${this.SERVICE_API}/${encodeURIComponent(key)}/book`, booking);
  }

  async createCourse(dto: CourseDto): Promise<CourseDto | MessageDto | null> {
    const result: CourseDto | MessageDto = await this.post<CourseDto | MessageDto>(Config.apiRoot + `${this.COURSE_API}`, null, dto);
    return result;
  }

  async updateCourse(key: string, dto: CourseDto): Promise<CourseDto | MessageDto | null> {
    const result: CourseDto | MessageDto = await this.put<CourseDto>(Config.apiRoot + `${this.COURSE_API}/${key}`, null, dto);
    return result;
  }

  async deleteCourse(key: string): Promise<MessageDto> {
    const result: MessageDto = await this.delete<MessageDto>(Config.apiRoot + `${this.COURSE_API}/${key}`);
    return result;
  }

  async getUserCourses(sub: string): Promise<GetUserCoursesApiResponseDto> {
    const result: GetUserCoursesApiResponseDto = await this.get<GetUserCoursesApiResponseDto>(Config.apiRoot + `${this.USER_API}/courses/${sub}`);
    return result;
  }

  async getUserCourseRecords(sub: string, signal?: AbortSignal): Promise<UserCourseRecordDto[]> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `${this.USER_API}/course-records/${encodeURIComponent(sub)}`);
    return (await axios.request<{ data: UserCourseRecordDto[] }>({ ...config, signal })).data.data;
  }

  async getCourseUsers(key: string): Promise<GetCourseUsersApiResponseDto> {
    const result: GetCourseUsersApiResponseDto = await this.get<GetCourseUsersApiResponseDto>(Config.apiRoot + `${this.COURSE_API}/users/${key}`);
    return result;
  }

  async saveUserCourses(sub: string, course_keys: string[]): Promise<GetUserCoursesApiResponseDto | MessageDto> {
    const requestDto: SetUserCourseAssignmentsDto = { courses: course_keys };
    const result: GetUserCoursesApiResponseDto | MessageDto = 
        await this.post<GetUserCoursesApiResponseDto | MessageDto>(Config.apiRoot + `${this.USER_API}/courses/${sub}`, null, requestDto);
    return result;
  }

  async saveCourseUsers(course_key: string, user_keys: string[]): Promise<GetCourseUsersApiResponseDto | MessageDto> {
    const requestDto: SetCourseAssignmentsDto = { users: user_keys };
    const result: GetCourseUsersApiResponseDto | MessageDto = 
        await this.post<GetCourseUsersApiResponseDto | MessageDto>(Config.apiRoot + `${this.COURSE_API}/users/${course_key}`, null, requestDto);
    return result;
  }

  async getAdminTasks(filters: Record<string, string>, signal?: AbortSignal): Promise<AdminTasksDto> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-tasks.php?${new URLSearchParams(filters)}`);
    return (await axios.request<AdminTasksDto>({ ...config, signal })).data;
  }

  async getAdminTaskSummary(environment: string, signal?: AbortSignal): Promise<AdminTaskSummary> {
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-tasks.php?${new URLSearchParams({ environment, summary: '1' })}`);
    return (await axios.request<AdminTaskSummary>({ ...config, signal })).data;
  }

  async changeAdminTaskDismissal(action: 'dismiss' | 'restore', task: { item_key: string; fingerprint: string; environment: string; note?: string }): Promise<void> {
    await this.post(Config.apiRoot + `/api/admin-tasks.php/${action}`, null, task);
  }

  async getAdminFinance(filters: Record<string, string>, signal?: AbortSignal): Promise<AdminFinanceDto> {
    const query = new URLSearchParams(filters);
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-finance.php?${query}`);
    return (await axios.request<AdminFinanceDto>({ ...config, signal })).data;
  }

  async getAdminFinancePurchase(key: string, filters: Record<string, string>, signal?: AbortSignal): Promise<FinancePurchaseDetails> {
    const query = new URLSearchParams(filters);
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-finance.php/${encodeURIComponent(key)}?${query}`);
    return (await axios.request<FinancePurchaseDetails>({ ...config, signal })).data;
  }

  async getAdminAnalytics(days: number, course: string | null, inactiveDays: number, timezone: string, signal?: AbortSignal, paging?: Record<string, string>): Promise<AdminAnalyticsDto> {
    const query = new URLSearchParams({ days: String(days), inactive_days: String(inactiveDays), timezone });
    if (course) query.set('course', course);
    Object.entries(paging ?? {}).forEach(([key, value]) => query.set(key, value));
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/admin-analytics.php?${query}`);
    const response = await axios.request<AdminAnalyticsDto>({ ...config, signal });
    return response.data;
  }

  async getLearningInsights(page: number, timezone: string, signal?: AbortSignal): Promise<LearningInsightsDto> {
    const query = new URLSearchParams({ page: String(page), timezone });
    const config = await this.apiCreateRequestConfig('GET', Config.apiRoot + `/api/learning-insights.php?${query}`);
    const response = await axios.request<LearningInsightsDto>({ ...config, signal });
    return response.data;
  }

  async getUserTodoList(user_key: string): Promise<LearningPathDto | MessageDto> {
    const path: string = (user_key ?? '').length > 0 ? `${this.USER_API}/to-do/${user_key}` : `${this.ME_API}/to-do`;
    const result: LearningPathDto | MessageDto = await this.get<LearningPathDto | MessageDto>(Config.apiRoot + path);
    return result;
  }

  async uploadAudio(file: File): Promise<MessageDto> {
    const requestDto: UploadFileDto = { name: file.name, data: file };
    const formData: FormData = this.objectToFormData(requestDto);
    const result: MessageDto = await this.post<MessageDto>(Config.apiRoot + `${this.UPLOAD_API}/audio`, formData, null);
    return result;
  }

  async importImage(url: string, signal?: AbortSignal): Promise<string> {
    const uri = Config.apiRoot + '/api/image-import.php';
    const config = await this.apiCreateRequestConfig('POST', uri);
    const response = await axios.post<{ dataUrl: string }>(uri, { url }, { ...config, signal });
    if (!/^data:image\/[a-z0-9.+-]+;base64,/i.test(response.data.dataUrl)) {
      throw new Error('The image service returned an invalid image.');
    }
    return response.data.dataUrl;
  }

  async downloadFile(url: string, signal?: AbortSignal): Promise<{ blob: Blob; disposition: string }> {
    const parsed = this.parseProtectedStorageDownloadUrl(url);
    if (parsed) {
        const path = parsed.searchParams.get('path');
        if (path === null) throw new Error('The protected download URL must include a storage path.');
        return this.downloadProtectedStorageFile(path, signal);
    }
    const uri = Config.apiRoot + '/api/file-download.php';
    const config = await this.apiCreateRequestConfig('POST', uri);
    const response = await axios.post<Blob>(uri, { url }, { ...config, signal, responseType: 'blob' });
    return { blob: response.data, disposition: String(response.headers['content-disposition'] ?? '') };
  }

  getProtectedStorageDownloadUrl(path: string): string {
    return new URL(Config.apiRoot + this.PROTECTED_STORAGE_API + '/download?path=' + encodeURIComponent(path),
        window.location.href).href;
  }

  private parseProtectedStorageDownloadUrl(url: string): URL | null {
    // Send credentials only to our configured API, never to an arbitrary protected-storage lookalike.
    try {
        const endpoint = new URL(this.getProtectedStorageDownloadUrl(''), window.location.href);
        const parsed = new URL(url, window.location.href);
        return !parsed.username && !parsed.password && parsed.origin === endpoint.origin
            && parsed.pathname === endpoint.pathname ? parsed : null;
    } catch { return null; }
  }

  getProtectedStorageFilePath(url: string): string | null {
    return this.parseProtectedStorageDownloadUrl(url)?.searchParams.get('path') ?? null;
  }

  async listProtectedStorage(path: string = '', type?: 'file' | 'folder', signal?: AbortSignal): Promise<ProtectedStorageListingDto> {
    const route = type === 'file' ? '/files' : type === 'folder' ? '/folders' : '';
    const uri = Config.apiRoot + this.PROTECTED_STORAGE_API + route + '?path=' + encodeURIComponent(path);
    const config = await this.apiCreateRequestConfig('GET', uri);
    const response = await axios.request<ProtectedStorageListingDto>({ ...config, signal });
    return response.data;
  }

  async createProtectedStorageFolder(path: string): Promise<ProtectedStorageEntryDto> {
    return this.post(Config.apiRoot + this.PROTECTED_STORAGE_API + '/folders', null, { path });
  }

  async uploadProtectedStorageFile(file: File, path: string = '', name?: string,
    options?: { signal?: AbortSignal; onProgress?: (percent: number) => void }): Promise<ProtectedStorageEntryDto> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('path', path);
    if (name !== undefined) form.append('name', name);
    const uri = Config.apiRoot + this.PROTECTED_STORAGE_API + '/files';
    const config = await this.apiCreateRequestConfig('POST', uri, form, true);
    const response = await axios.request<ProtectedStorageEntryDto>({ ...config, signal: options?.signal,
        onUploadProgress: options?.onProgress ? event => {
            if (event.total) options.onProgress!(Math.min(100, Math.round(event.loaded / event.total * 100)));
        } : undefined });
    return response.data;
  }

  async renameProtectedStorageFolder(path: string, newPath: string): Promise<ProtectedStorageEntryDto> {
    return this.put(Config.apiRoot + this.PROTECTED_STORAGE_API + '/folders', null, { path, new_path: newPath });
  }

  async renameProtectedStorageFile(path: string, newPath: string): Promise<ProtectedStorageEntryDto> {
    return this.put(Config.apiRoot + this.PROTECTED_STORAGE_API + '/files', null, { path, new_path: newPath });
  }

  async deleteProtectedStorageFolder(path: string, recursive: boolean = false): Promise<ProtectedStorageDeleteDto> {
    return this.delete(Config.apiRoot + this.PROTECTED_STORAGE_API + '/folders?path=' + encodeURIComponent(path)
        + '&recursive=' + String(recursive));
  }

  async deleteProtectedStorageFile(path: string): Promise<ProtectedStorageDeleteDto> {
    return this.delete(Config.apiRoot + this.PROTECTED_STORAGE_API + '/files?path=' + encodeURIComponent(path));
  }

  async downloadProtectedStorageFile(path: string, signal?: AbortSignal): Promise<{ blob: Blob; disposition: string }> {
    const uri = this.getProtectedStorageDownloadUrl(path);
    const config = await this.apiCreateRequestConfig('GET', uri);
    const response = await axios.request<Blob>({ ...config, signal, responseType: 'blob' });
    return { blob: response.data, disposition: String(response.headers['content-disposition'] ?? '') };
  }

  async askGenAi(request: AiRequestDto): Promise<AiResponseDto | MessageDto> {
    const result: AiResponseDto | MessageDto = await this.post<AiResponseDto | MessageDto>(
        Config.apiRoot + `${this.AI_API}`,
        null,
        request
    );
    return result;
  }

  async getThemes(): Promise<GetThemesDto> {
    const result: GetThemesDto = await this.get<GetThemesDto>(Config.apiRoot + `${this.THEME_API}`);
    return result;
  }

  async getThemeCss(fileName: string): Promise<ThemeCategory[]> {
    const result: ThemeCategory[] = await this.get<ThemeCategory[]>(Config.apiRoot + `${this.THEME_API}/${fileName}`);
    return result;
  }

  async createTheme(fileName: string, selectors: ThemeNode[], originalName: string): Promise<MessageDto | null> {
    return await this.post<MessageDto | null>(Config.apiRoot + `${this.THEME_API}/${fileName}?source=${encodeURIComponent(originalName)}`, null, selectors);
  }

  async updateTheme(fileName: string, newFileName: string, selectors: ThemeNode[]): Promise<MessageDto | null> {
    return await this.put<MessageDto | null>(Config.apiRoot + `${this.THEME_API}/${fileName}?rename=${encodeURIComponent(newFileName ?? fileName)}`, null, selectors);
  }

  async deleteTheme(fileName: string): Promise<MessageDto | null> {
    return await this.delete<MessageDto | null>(Config.apiRoot + `${this.THEME_API}/${fileName}`);
  }
};
